

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Progress,
  Divider,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Center,
} from "@chakra-ui/react";
import { BASE_URL } from "../../Globalapi/Globalapi";

// --- Card Component Base Style (Defining it outside is safe) ---
const CardContainer = (props) => {
  // Hooks are used unconditionally inside this component
  const bg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.100", "gray.600");

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="xs"
      boxShadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      w="100%"
      {...props}
    />
  );
};
// --------------------------------------------------------------------------

const academicId = sessionStorage.getItem("Academic");

/**
 * Calculates the overall performance percentage for a single class
 * by aggregating all obtained marks against all maximum marks.
 * @param {Array} results - The raw exam_result array from the API.
 * @returns {Array} - An array containing a single performance object
 * [{ class: string, percentage: number }] or an empty array.
 */
const processClassPerformance = (results) => {
  if (!results || results.length === 0) return [];

  const performance = results.reduce(
    (acc, current) => {
      // 1. Calculate obtained marks (written + internal)
      const obtained =
        parseInt(current.written_mark || 0) +
        parseInt(current.internal_mark || 0);

      // 2. Get the actual max marks for this specific subject result
      const possible = parseInt(current.max_marks || 0);

      // Skip invalid entries (max marks must be greater than zero)
      if (possible === 0) return acc;

      // 3. Accumulate total obtained and total possible across ALL results
      acc.totalObtained += obtained;
      acc.totalPossible += possible;

      return acc;
    },
    {
      totalObtained: 0,
      totalPossible: 0,
    }
  );

  const totalObtained = performance.totalObtained;
  const totalPossible = performance.totalPossible;

  if (totalPossible === 0) return [];

  // 4. Calculate the overall percentage
  const percentage = (totalObtained / totalPossible) * 100;

  // 5. Return a single object (representing the class's performance)
  return [
    {
      // Using a generic label since we're not grouping
      class: "Overall Performance",
      percentage: Math.round(percentage * 10) / 10,
    },
  ];
};

function ClassPerformanceCard() {
  // --- 1. HOOKS (MUST BE UNCONDITIONAL) ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [examResults, setExamResults] = useState([]);

  const userId = sessionStorage.getItem("user_id");

  // Custom hook usage for theme values (MUST be before any return)
  const itemHoverBg = useColorModeValue("blue.50", "gray.600");
  const classNameColor = useColorModeValue("gray.800", "white"); // Used for 'Overall Average' text

  // --- Data Fetching Effect ---
  useEffect(() => {
    if (!userId || !academicId) {
      setError("User ID or Academic ID is missing from session data.");
      return;
    }

    const fetchExamData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${BASE_URL}/admin/exam/list_result_to_classteacher.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ pass: userId }),
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();

        if (data.exam_result && Array.isArray(data.exam_result)) {
          const filteredDetails = data.exam_result.filter(
            (detail) => detail.academic_year_id === academicId
          );
          setExamResults(filteredDetails);
        } else {
          setError(
            data.message || "Failed to fetch results or invalid data format."
          );
        }
      } catch (err) {
        setError(`Failed to fetch exam results: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [userId, academicId]);

  // --- Data Processing Memo ---
  const classPerformers = useMemo(() => {
    return processClassPerformance(examResults);
  }, [examResults]);

  // --- Rendering Logic Helpers ---
  const getColorScheme = (percentage) => {
    if (percentage > 85) return "teal";
    if (percentage > 70) return "blue";
    return "orange";
  };

  // --- 2. EARLY RETURNS (Conditional Rendering) ---
  if (loading) {
    return (
      <CardContainer>
        <Center py={10}>
          <Spinner size="xl" color="teal.500" />
          <Text ml={3}>Loading Class Results...</Text>
        </Center>
      </CardContainer>
    );
  }

  if (error) {
    return (
      <CardContainer>
        <Alert status="error">
          <AlertIcon /> Error: {error}
        </Alert>
      </CardContainer>
    );
  }

  if (classPerformers.length === 0) {
    return (
      <CardContainer>
        <Alert status="info">
          <AlertIcon /> No class performance data found for the current academic
          year.
        </Alert>
      </CardContainer>
    );
  }

  // --- 3. MAIN RENDER ---
  const performer = classPerformers[0];
  const scheme = getColorScheme(performer.percentage);

  return (
    <CardContainer>
      <Heading size="md" mb={1} color="teal.500">
        Your Class Performance Average 🏆
      </Heading>
      <Text fontSize="md" color="gray.500" mb={4}>
        Overall average result for your class across all subjects.
      </Text>
      <Divider mb={6} />

      <Box
        p={4}
        borderRadius="lg"
        transition="all 0.2s"
        // Uses the hook result defined at the top
        _hover={{ bg: itemHoverBg, transform: "scale(1.01)" }}
      >
        <Flex justify="space-between" align="center" mb={2}>
          {/* Left Side: Display the Class Label/Context */}
          <Text
            fontWeight="bold"
            fontSize="xl"
            color={classNameColor} // Uses the hook result defined at the top
          >
            Overall Average
          </Text>

          {/* Right Side: Percentage */}
          <Text fontWeight="extrabold" fontSize="3xl" color={`${scheme}.500`}>
            {performer.percentage}%
          </Text>
        </Flex>
        {/* Progress Bar */}
        <Progress
          value={performer.percentage}
          size="lg"
          colorScheme={scheme}
          borderRadius="lg"
          hasStripe={performer.percentage < 100}
          isAnimated
        />
      </Box>
    </CardContainer>
  );
}

export default ClassPerformanceCard;
